import { useTranslation } from "react-i18next";
import { useOperationsMap } from "../OperationsMapProvider";
import { Avatar, Box, DialogContent, DialogTitle, Grid, IconButton, Stack, Typography } from "@mui/material";
import { Close as CloseIcon, ArrowBack as BackIcon, Phone as PhoneIcon, Circle as CircleIcon, ContentCopy as CopyIcon } from "@mui/icons-material";
import { makeStyles } from "tss-react/mui";
import { useMemo } from "react";
import ResponsiveDialog from "../../../common/core/ResponsiveDialog";
import { useDevice } from "../../../../providers/DeviceProvider";
import { useDispatch } from "../../dispatch/providers/DispatchProvider";
import TaskItem from "../../dispatch/common/TaskItem";
import { getFleetUnitStateText, hasLocation, minutesSinceLastLocation } from "./VehiclesPanel";
import Decimal from "decimal.js";
import { copyToClipboard } from "../../../../common/helpers";
import { useNotification } from "../../../../providers/NotificationProvider";

const useStyles = makeStyles()(
  (theme) => ({
    closeButton: {
      marginLeft: theme.spacing(-1),
      marginRight: theme.spacing(1)
    },
    inset: {
      position: "relative",
      borderRadius: theme.spacing(2),
      background: "#F7F8FA",
      padding: theme.spacing(2)
    },
    stateIcon: {
      fontSize: "12px",
      marginRight: theme.spacing(0.5)
    },
    gpsText: {
    },
    copyButton: {
      position: "absolute",
      right: "16px",
      marginTop: "-5px",
      padding: theme.spacing(1)
    },
    copyIcon: {
      fontSize: "16px",
      marginLeft: theme.spacing(0.25),
    }
  })
);

export default function VehicleDialog() {
  const { t } = useTranslation();
  const { classes } = useStyles();

  const { slim } = useDevice();
  const { snackbar } = useNotification();

  const {
    closeVehicleDialog,
    vehicleId,
    vehicles
  } = useOperationsMap();

  const { drivers } = useDispatch();

  const vehicle = useMemo(() => vehicles.find(v => v.id === vehicleId), [vehicleId, vehicles]);
  const driver = useMemo(() => drivers.find(d => d.id === vehicle?.driver?.id), [vehicle, drivers])

  const hasLoc = !!vehicle ? hasLocation(vehicle) : false;
  const minsSinceLoc = hasLoc ? minutesSinceLastLocation(vehicle) : 9999999;
  const location = hasLoc ? vehicle?.lastReportedLocation?.coords : null;

  const formattedLocation = useMemo(() => {
    return hasLoc ? `${new Decimal(location.longitude).toFixed(4)}, ${new Decimal(location.latitude).toFixed(4)}` : "--, --"
  }, [location]);

  return !!vehicle ? (
    <ResponsiveDialog
      open
      onClose={closeVehicleDialog}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle>
        <Stack sx={{ width: "100%" }}>

          <Stack justifyContent={slim ? "flex-end" : "space-between"} alignItems="center" direction={slim ? "row-reverse" : "row"} sx={{ width: "100%" }} spacing={1}>
            {vehicle?.title || ""}
            <IconButton onClick={closeVehicleDialog} size="small" color="primary" className={classes.closeButton}>
              {slim ? <BackIcon sx={{ marginLeft: -1 }} /> : <CloseIcon />}
            </IconButton>
          </Stack>

          <Typography variant="body1">{vehicle.description || ""}</Typography>

        </Stack>
      </DialogTitle>

      <DialogContent>
        <Stack spacing={2}>

          <Stack direction="row" justifyContent="space-between" alignItems="center" className={classes.inset}>
            <Stack direction="row" spacing={2} alignItems="center">
              <Avatar
                src={driver?.picture}
                alt={driver?.name}
              />
              <Stack>
                <Typography variant="subtitle1">{driver?.name || "-"}</Typography>
                <Typography variant="caption" color="textDisabled">{driver?.phoneNumber || "-"}</Typography>
              </Stack>
            </Stack>

            <IconButton href={!!driver?.phoneNumber ? `tel:${driver?.phoneNumber}` : undefined} disabled={!driver?.phoneNumber}>
              <PhoneIcon />
            </IconButton>

          </Stack>

          <Box>
            <Grid container spacing={2}>
              <Grid item xs={12} lg={6}>
                <Stack className={classes.inset}>
                  <Typography variant="caption" color="textDisabled" textTransform="uppercase">STATUS</Typography>
                  <Typography variant="body1" fontWeight={600}>
                    <CircleIcon
                      className={classes.stateIcon}
                      color={vehicle.tasks.length > 0 ? "info" : !!vehicle.driver?.id ? "warning" : "textDisabled"}
                    />
                    {getFleetUnitStateText(t, vehicle.fleetUnitData?.state?.name)}
                  </Typography>
                </Stack>
              </Grid>

              <Grid item xs={12} lg={6}>
                <Stack className={classes.inset}>
                  <Typography variant="caption" color="textDisabled">GPS {minsSinceLoc <= 3 ? `(${t("common:now")})` : minsSinceLoc < 60 ? `(${minsSinceLoc}m siden)` : null}</Typography>
                  <Typography variant="body1" className={classes.gpsText}>
                    {formattedLocation}
                    {
                      !!navigator.clipboard ?
                        (
                          <IconButton
                            className={classes.copyButton}
                            disabled={!hasLoc}
                            size="small"
                            onClick={() => (copyToClipboard(formattedLocation), snackbar(t("common:copiedToClipboard")))}
                          >
                            <CopyIcon
                              fontSize="small"
                              className={classes.copyIcon}
                            />
                          </IconButton>
                        ) : null
                    }
                  </Typography>
                </Stack>
              </Grid>
            </Grid>
          </Box>




          <Stack spacing={1} className={classes.inset}>

            <Typography variant="subtitle2" color="textDisabled">Tildelte tjenester</Typography>

            {vehicle?.tasks.map((task, key) => <TaskItem task={task} key={key} card />)}

          </Stack>



        </Stack>
      </DialogContent>

    </ResponsiveDialog >
  ) : null;
}

